/* eslint-disable @next/next/no-img-element */
/* eslint-disable react/require-default-props */
import {
  Avatar,
  CardType, DiscussionCard, SearchResultsCardHeader,
  ThreadMetrics,
} from '@amzn/cuminuicomponents';
import { KatIcon } from '@amzn/katal-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import AmazonRepliedBadge from 'public/images/icons/amazonRepliedBadge.svg';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonTypes, MetricKeys } from 'src/constants/metrics';
import { getThreadDetail, getUserProfileDetail } from 'src/constants/pages';
import { useAppSelector } from 'src/redux/hooks';
import { PostState, PostType } from 'src/types/DiscussionPost';
import { Post } from 'src/types/Search';
import {
  constructQueryParams, transformHtmlToReact,
  useMetrics,
} from 'src/utils';

export interface SearchListingPostProps {
  post: Post
  postIndex: number;
  cardType: CardType.search | CardType.pinnedSearch
}

const SearchListingPost: React.FC<SearchListingPostProps> = ({
  post,
  postIndex,
  cardType,
}) => {
  const { t } = useTranslation();
  const { clickMetric } = useMetrics();
  const { groups } = useAppSelector((state) => state.groups);
  const { basePath } = useRouter();

  const handleMetrics = (postId: string, index: number) => {
    clickMetric({
      metricValue: postId,
      metricName: ButtonTypes.postTitle,
      countMetrics: [{ metricName: MetricKeys.postIndex, metricValue: index }],
    });
  };

  const getLinkToThread = (threadId: string, postId: string, postType: PostType) => {
    if (postType === PostType.op) {
      return getThreadDetail(threadId);
    }
    return constructQueryParams({ postId }, getThreadDetail(threadId));
  };

  const getBadges = ({
    contentState,
    hasAmazonReply,
    hasSolution,
  }: {
    contentState: PostState,
    hasAmazonReply: boolean,
    hasSolution: boolean
  }) => {
    const badges = [];
    if (contentState === PostState.closed) {
      badges.push(<KatIcon key="badge-post-locked" name="lock_outline" size="small" className="text-kat-nordic" data-testid="badge-post-locked" />);
    }
    if (hasAmazonReply) {
      badges.push(<img key="badge-amazon-replied" className="h-[20px] w-[20px]" src={AmazonRepliedBadge.src} alt="Amazon replied" data-testid="badge-amazon-replied" />);
    }
    if (hasSolution) {
      badges.push(<KatIcon key="badge-has-solution" name="check_circle" size="small" className="text-kat-fiji" data-testid="badge-has-solution" />);
    }
    return badges.length > 0 ? (
      <div className="flex flex-row space-x-[7px] rtl:space-x-reverse">
        {badges}
      </div>
    ) : undefined;
  };

  const {
    userId,
    postId,
    userAvatarSrc,
    isMigrated,
    content,
    contentMetadata,
    userDisplayName,
    createdAt,
    groupId,
    replyToUserName,
    replyTo,
    threadId,
    postType,
    title,
    contentState,
    hasAmazonReply,
    hasSolution,
    downVotes,
    lastActivityTime,
    totalReplies,
    upVotes,
    totalViews,
  } = post;

  const authorProfileHref = userId && `${basePath}${getUserProfileDetail(userId)}`;

  return (
    <DiscussionCard
      avatar={(
        <Avatar
          src={userAvatarSrc}
          href={authorProfileHref}
          onClick={() => clickMetric({
            metricValue: userId,
            metricName: ButtonTypes.postAuthor,
          })}
        />
      )}
      cardHighlight={<div />}
      cardType={cardType}
      content={(
        <span
          data-testid="search-card-content"
          className={`break-words images:max-w-full ${isMigrated ? 'discourse-forums' : ''}`}
        >
          {transformHtmlToReact({ html: content, metadata: contentMetadata })}
        </span>
      )}
      header={(
        <SearchResultsCardHeader
          authoredBy={userDisplayName}
          authoredByHref={authorProfileHref}
          authoredByOnClick={() => clickMetric({
            metricValue: userId,
            metricName: ButtonTypes.postAuthor,
          })}
          createdAt={createdAt}
          groupId={groupId && t(groups[groupId]?.nameStringId)}
          replyingToAuthor={replyToUserName}
          replyToHref={`${basePath}${constructQueryParams({ postId: replyTo }, getThreadDetail(threadId))}`}
          title={(
            <Link
              key={postId}
              href={getLinkToThread(threadId, postId, postType)}
              data-testid="heading"
              role="link"
              className="cursor-pointer break-all text-sm font-bold !text-kat-nordic line-clamp-1"
              onClick={() => handleMetrics(postId, postIndex)}
            >
              {title}
            </Link>
          )}
          badges={getBadges({
            contentState,
            hasAmazonReply,
            hasSolution,
          })}
        />
      )}
      threadMetrics={(
        <ThreadMetrics
          downvoteCount={downVotes}
          lastActivity={lastActivityTime}
          replyCount={totalReplies}
          upvoteCount={upVotes}
          viewCount={totalViews}
        />
      )}
    />
  );
};
export default SearchListingPost;
